import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootStateType } from '@Store';
import API from '@Services/apiAxios';
import { GeneralSuggestionItem, BotIntent } from '../types/intent.types';
import { formatSuggestionLabel } from '../utils/formatSuggestionLabel';
import { isExcelFile } from '../utils/fileType';
import { parseExcel } from '../services/excelParser';
import { uploadExcelData } from '../services/uploadService';
import { getExtractUploadUrl, uploadToPresignedS3 } from '../services/s3UploadService';
import { extractDocument } from '../services/extractionService';
import { getChatbotContext } from '../utils/chatbotContext';

// ==================== TYPES ====================
export interface ConfirmationDetails {
  tool: string;
  summary: string;
}

export interface BotMessage {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp: Date;
  state?: 'complete' | 'awaiting_input' | 'awaiting_confirmation' | 'prefill_ui' | string;
  uiAction?: {
    editUrl: string;
    sections?: {
      consignee?: any;
      shipper?: any;
      package?: any;
    };
  };
  relatedChips?: Array<{
    id: string;
    label: string;
    query: string;
    intent?: string;
  }>;
  missingFields?: string[];
  attachmentPreviews?: Array<{ name: string; size: string; type: string }>;
  s3Key?: string;
  confirmation?: ConfirmationDetails;
}

export interface ApiResponse {
  message: string;
  state: string;
  sessionId?: string;
  confirmation?: ConfirmationDetails;
  data?: {
    sessionId?: string;
  };
  uiAction?: {
    editUrl: string;
    sections?: any;
  };
  missingFields?: string[];
  suggestions?: Array<{
    label: string;
    query: string;
    intent?: string;
  }>;
  suggestedActions?: Array<{
    label: string;
    query: string;
    intent?: string;
  }>;
  relatedChips?: Array<{
    id: string;
    label: string;
    query: string;
    intent?: string;
  }>;
}

export interface ConversationState {
  sessionId: string;
  activeIntent: BotIntent | null;
  pendingIntent: BotIntent | null;
  conversationState: string | null;
  pendingWorkflow: string | null;
  missingFields: string[];
}

const appendFieldToDraft = (currentDraft: string, field: string): string => {
  const formattedLabel = formatSuggestionLabel(field);
  const labelMarker = `${formattedLabel}:`;
  // Case-insensitive regex check to see if the label already exists in the draft.
  // Matches label marker followed by space or newline.
  const regex = new RegExp(`(^|\\s)${formattedLabel}:(\\s|$)`, 'i');
  if (regex.test(currentDraft)) {
    return currentDraft;
  }

  let updated = currentDraft.trimEnd();
  if (updated.length > 0) {
    updated += '\n\n';
  }
  updated += `${labelMarker}\n`;
  return updated;
};

export interface useChatbotStateProps {
  orgCode: string;
  onNavigate?: (url: string) => void;
  apiBaseUrl?: string;
}

export const useChatbotState = ({ orgCode, onNavigate, apiBaseUrl }: useChatbotStateProps) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<BotMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [chatbotExtractionLoading, setChatbotExtractionLoading] = useState(false);
  const [extractedEntities, setExtractedEntities] = useState<any>(null);
  const [activeConfirmation, setActiveConfirmation] = useState<ConfirmationDetails | null>(null);
  const [confirmationLoading, setConfirmationLoading] = useState<'confirm' | 'cancel' | null>(null);

  // Gemini animation state
  const [isActivating, setIsActivating] = useState(false);

  const userName = useSelector(
    (state: RootStateType) =>
      state.loginUser.profile?.name || state.loginUser.profile?.email || state.loginUser.profile?.username || 'User',
  );

  // Bot-specific state
  const [conversation, setConversation] = useState<ConversationState>(() => {
    const savedSessionId = localStorage.getItem('speedbot_session_id') || '';
    return {
      sessionId: savedSessionId,
      activeIntent: null,
      pendingIntent: null,
      conversationState: null,
      pendingWorkflow: null,
      missingFields: [],
    };
  });

  const { sessionId, activeIntent, pendingIntent, conversationState, missingFields } = conversation;
  const currentBotState = conversationState;

  const [conversationHistory, setConversationHistory] = useState<BotMessage[]>([]);
  const [activeSuggestions, setActiveSuggestions] = useState<GeneralSuggestionItem[]>([]);

  const messageCounterRef = useRef(0);
  const generateLocalMessageId = useCallback(() => {
    messageCounterRef.current += 1;
    return `msg-${Date.now()}-${messageCounterRef.current}`;
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    if (isOpen) {
      const savedSessionId = localStorage.getItem('speedbot_session_id') || '';
      setConversation(prev => ({ ...prev, sessionId: savedSessionId }));

      console.group('🤖 SPEEDBOT INITIALIZATION');
      console.info('📌 Session ID Loaded:', savedSessionId || 'None (will be created on first message)');
      console.info('🏢 Organization Code:', orgCode);
      console.info('⏰ Timestamp:', new Date().toISOString());
      if (apiBaseUrl) {
        console.info('🔗 API Base URL:', apiBaseUrl);
      }
      console.groupEnd();
    }
  }, [isOpen, orgCode, apiBaseUrl]);

  // Trigger Gemini activation animation on open
  useEffect(() => {
    if (isOpen) {
      setIsActivating(true);
      const timer = setTimeout(() => {
        setIsActivating(false);
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Focus input field when chatbot is opened
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Log current bot state when it changes
  useEffect(() => {
    if (currentBotState) {
      console.info('🤖 Bot state transitioned to:', currentBotState);
    }
  }, [currentBotState]);

  // ==================== FILE HANDLING ====================
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setApiError('File size must be less than 10MB');
        return;
      }

      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];

      if (!allowedTypes.includes(file.type)) {
        setApiError('Only PDF, JPG, PNG, Word, and Excel files are allowed');
        return;
      }
    }

    setAttachedFiles(prev => [...prev, ...files]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    console.info(
      '📎 Files selected:',
      files.map(f => ({
        name: f.name,
        size: formatFileSize(f.size),
        type: f.type,
      })),
    );
  }, []);

  const removeFile = useCallback((fileName: string) => {
    setAttachedFiles(prev => prev.filter(file => file.name !== fileName));
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // ==================== MESSAGE HANDLING ====================
  const handleSendMessage = async (
    messageText?: string,
    intentOverride?: string | null,
    confirmIntentSwitchOverride?: boolean | null,
    confirmActionOverride?: boolean | null,
  ) => {
    const textToSend = messageText || inputValue.trim();
    const isIntentConfirmation = confirmIntentSwitchOverride !== undefined && confirmIntentSwitchOverride !== null;

    if (!textToSend && attachedFiles.length === 0 && !isIntentConfirmation) return;

    try {
      setIsLoading(true);
      setApiError(null);
      setConversation(prev => ({ ...prev, missingFields: [] })); // Clear missing fields before request

      let excelResponse: any = null;
      const hasNonExcel = attachedFiles.some(file => !isExcelFile(file));

      if (attachedFiles.length > 0) {
        if (hasNonExcel) {
          try {
            setChatbotExtractionLoading(true);
            setUploadProgress(0);

            // Find the non-Excel file (we process the first one or assume single file upload)
            const nonExcelFile = attachedFiles.find(file => !isExcelFile(file))!;

            // Step 1: Request Upload URL
            const presignedData = await getExtractUploadUrl(nonExcelFile.name, nonExcelFile.type, orgCode, sessionId);

            // Step 2 & 3: Upload File to S3
            await uploadToPresignedS3(presignedData.url, presignedData.fields, nonExcelFile, percent =>
              setUploadProgress(percent),
            );

            // Step 4: Call Extraction API
            const extractionRes = await extractDocument(presignedData.key, 'shipment', orgCode, sessionId);

            // Append user message
            const userMessage: BotMessage = {
              id: generateLocalMessageId(),
              role: 'user',
              content: textToSend,
              timestamp: new Date(),
              attachmentPreviews: attachedFiles.map(file => ({
                name: file.name,
                size: formatFileSize(file.size),
                type: file.type,
              })),
            };
            setMessages(prev => [...prev, userMessage]);
            setConversationHistory(prev => [...prev, userMessage]);

            // Append bot message with extraction confirmation
            const botMessage: BotMessage = {
              id: generateLocalMessageId(),
              role: 'bot',
              content: extractionRes.message || 'Document uploaded and extraction completed successfully.',
              state: extractionRes.state || 'complete',
              timestamp: new Date(),
            };
            setMessages(prev => [...prev, botMessage]);
            setConversationHistory(prev => [...prev, botMessage]);

            // Inject fields into extractedEntities state
            if (extractionRes.fields) {
              setExtractedEntities(extractionRes.fields);
            }

            setInputValue('');
            setAttachedFiles([]);
            setChatbotExtractionLoading(false);
            setUploadProgress(0);
            setIsLoading(false);
            return; // Stop workflow: do NOT call POST /speedboxbot/bot/message!
          } catch (error: any) {
            console.error('❌ Extraction workflow error:', error);
            const errorMsg = error?.response?.data?.message || error?.message || error;
            setApiError(`Extraction failed: ${errorMsg}`);
            setChatbotExtractionLoading(false);
            setUploadProgress(0);
            setIsLoading(false);
            return;
          }
        } else {
          // Excel Flow (unchanged)
          setShowFileDialog(true);
          for (const file of attachedFiles) {
            try {
              const parsedData = await parseExcel(file);
              const hasData = Object.values(parsedData.sheets).some(rows => rows.length > 0);
              if (!hasData) {
                throw new Error('EmptyWorkbook: The uploaded Excel file does not contain any usable data.');
              }
              const response = await uploadExcelData(parsedData, orgCode, sessionId);
              excelResponse = response;
            } catch (error: any) {
              console.error('❌ Excel processing error:', error);
              let errorMsg = 'Failed to process Excel file.';
              if (error?.message?.startsWith('EmptyWorkbook:')) {
                errorMsg = 'The uploaded Excel file does not contain any usable data.';
              } else if (error?.message?.includes('Unsupported')) {
                errorMsg = 'Unsupported Excel format. Please check the file structure.';
              } else {
                errorMsg = `Excel Parsing Error: ${error.message || error}`;
              }
              setApiError(errorMsg);
              setShowFileDialog(false);
              setIsLoading(false);
              return;
            }
          }
          setShowFileDialog(false);
        }
      }

      if (textToSend || attachedFiles.length > 0) {
        const userMessage: BotMessage = {
          id: generateLocalMessageId(),
          role: 'user',
          content: textToSend,
          timestamp: new Date(),
          attachmentPreviews:
            attachedFiles.length > 0
              ? attachedFiles.map(file => ({
                  name: file.name,
                  size: formatFileSize(file.size),
                  type: file.type,
                }))
              : undefined,
        };

        setMessages(prev => [...prev, userMessage]);
        setConversationHistory(prev => [...prev, userMessage]);
      }
      setInputValue('');
      setAttachedFiles([]);

      // Determine intent:
      // - If intentOverride is explicitly provided (string or null), we use it.
      // - Otherwise, we use the current activeIntent.
      let intentToSend: string | null = null;
      if (intentOverride !== undefined) {
        intentToSend = intentOverride;
      } else {
        intentToSend = activeIntent;
      }

      // Check if message is a get-rates query to trigger route_get_rates intent
      const isGetRatesTrigger =
        textToSend.toLowerCase().includes('get rate') ||
        textToSend.toLowerCase().includes('check rate') ||
        textToSend.toLowerCase().includes('check pricing') ||
        textToSend.toLowerCase().includes('get a quote') ||
        textToSend.toLowerCase().includes('calculate rate') ||
        textToSend.toLowerCase() === 'rates' ||
        textToSend.toLowerCase() === 'pricing';

      if (isGetRatesTrigger) {
        intentToSend = 'route_get_rates';
      }

      console.group('🤖 SPEEDBOT API REQUEST');
      const apiPayload: any = {
        message: textToSend,
        intent: intentToSend,
        context: getChatbotContext(location),
      };

      if (excelResponse) {
        apiPayload.excelData = excelResponse;
        apiPayload.excelResponse = excelResponse;
      }

      if (extractedEntities) {
        apiPayload.entities = extractedEntities;
        setExtractedEntities(null); // Clear after injecting silently
      }

      if (isIntentConfirmation) {
        apiPayload.confirmIntentSwitch = confirmIntentSwitchOverride;
      }

      if (confirmActionOverride !== undefined && confirmActionOverride !== null) {
        apiPayload.confirmAction = confirmActionOverride;
      }

      const requestHeaders: Record<string, string> = {
        org_code: orgCode,
      };
      if (sessionId) {
        requestHeaders['sessionId'] = sessionId;
      }

      console.info('📤 Request Body:', apiPayload);
      console.info('📤 Request Headers:', requestHeaders);
      console.info('⏰ Sent at:', new Date().toISOString());
      console.groupEnd();

      let data: ApiResponse;
      if (intentToSend === 'route_get_rates') {
        const { hasAdminAccess } = await import('../../../services/hasAdminAccess');
        const { handleGetRatesBotMessage } = await import('../services/getRatesBotFlow');
        data = handleGetRatesBotMessage(textToSend, sessionId, attachedFiles, hasAdminAccess());
      } else {
        const response = await API.post<ApiResponse>('/speedboxbot/chat', apiPayload, {
          headers: requestHeaders,
        });
        data = response.data;
      }

      console.group('🤖 SPEEDBOT API RESPONSE');
      console.info('📥 Response:', data);
      console.info('🔄 New State:', data.state);
      console.groupEnd();

      // Extract new session ID if returned from backend
      const responseSessionId = data.sessionId || data.data?.sessionId;
      if (responseSessionId) {
        localStorage.setItem('speedbot_session_id', responseSessionId);
      }

      // Handle missingFields suggestions from backend
      const responseMissingFields = data.missingFields || [];

      // Determine updated activeIntent and pendingIntent.
      let nextActiveIntent = activeIntent;
      let nextPendingIntent = pendingIntent;

      if (data.state === 'awaiting_intent_confirmation') {
        // We entered confirmation flow. Active intent remains, pending intent is the requested one.
        nextPendingIntent = ((data as any).pendingIntent || intentOverride) as BotIntent;
      } else {
        // We are NOT in confirmation flow. Clear pending intent.
        nextPendingIntent = null;

        // If we just finished a confirmation switch request:
        if (isIntentConfirmation) {
          if (confirmIntentSwitchOverride === true) {
            // User confirmed the switch to the new intent
            nextActiveIntent = intentOverride as BotIntent;
          } else {
            // User declined the switch, stay on active intent
            nextActiveIntent = activeIntent;
          }
        } else {
          // Normal message send/quick action.
          if ((data as any).pendingIntent) {
            nextActiveIntent = (data as any).pendingIntent;
          } else if (intentOverride && intentOverride !== null) {
            nextActiveIntent = intentOverride as BotIntent;
          }
        }
      }

      setConversation(prev => ({
        ...prev,
        ...(responseSessionId && { sessionId: responseSessionId }),
        activeIntent: nextActiveIntent,
        pendingIntent: nextPendingIntent,
        conversationState: data.state || null,
        missingFields: responseMissingFields,
      }));

      // Handle guided workflow suggestions from backend
      const responseSuggestions = data.suggestions || data.suggestedActions || data.relatedChips;

      // Handle guided workflow suggestions (do not render missing fields above input bar)
      if (responseSuggestions && Array.isArray(responseSuggestions)) {
        const formattedSuggestions = responseSuggestions
          .map((item: any) => ({
            label: item.label || item.text || (typeof item === 'string' ? item : ''),
            query: item.query || item.message || item.text || (typeof item === 'string' ? item : ''),
            intent: item.intent || null,
          }))
          .filter(item => item.label && item.query);

        setActiveSuggestions(formattedSuggestions);
      } else {
        setActiveSuggestions([]);
      }

      const botMessage: BotMessage = {
        id: generateLocalMessageId(),
        role: 'bot',
        content: data.message,
        timestamp: new Date(),
        state: data.state,
        uiAction: data.uiAction,
        relatedChips: data.relatedChips,
        missingFields: responseMissingFields,
        confirmation: data.confirmation,
      };

      if (data.confirmation) {
        setActiveConfirmation(data.confirmation);
      } else {
        setActiveConfirmation(null);
      }

      setMessages(prev => [...prev, botMessage]);
      setConversationHistory(prev => [...prev, botMessage]);

      if (data.state?.toLowerCase() === 'prefill_ui' && onNavigate && data.uiAction) {
        console.info('🔗 NAVIGATING TO FORM:', data.uiAction.editUrl);
        onNavigate(data.uiAction.editUrl);
      }
    } catch (error: any) {
      console.group('❌ BOT API ERROR');
      const errorMsg =
        error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Something went wrong';
      console.groupEnd();

      setApiError(`Failed to send message: ${errorMsg}`);

      const errorMessage: BotMessage = {
        id: generateLocalMessageId(),
        role: 'bot',
        content: `⚠️ Error: ${errorMsg}. Please try again.`,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setConfirmationLoading(null);
    }
  };

  const handleConfirmationAction = useCallback(
    async (confirm: boolean) => {
      if (!activeConfirmation) return;
      setConfirmationLoading(confirm ? 'confirm' : 'cancel');
      const text = confirm ? 'Please proceed with the requested action.' : 'Cancel this requested action.';
      await handleSendMessage(text, null, null, confirm);
    },
    [activeConfirmation, handleSendMessage],
  );

  const handleQuickAction = useCallback(
    (action: any) => {
      setInputValue(action.query);
      setTimeout(() => handleSendMessage(action.query, action.intent), 100);
    },
    [handleSendMessage],
  );

  const handleBotStateAction = (action: 'yes' | 'no' | 'open-form', url?: string) => {
    if (action === 'open-form' && url) {
      console.info('🔗 NAVIGATING:', url);
      if (onNavigate) {
        onNavigate(url);
      } else {
        window.location.href = url;
      }
    } else {
      const message = action === 'yes' ? 'Yes' : action === 'no' ? 'No' : '';
      console.info('✅ User Action:', message);
      handleSendMessage(message);
    }
  };
  const handleRelatedChipClick = (chip: any) => {
    console.info('🏷️ Related chip clicked:', chip.label);
    setInputValue(chip.query);
    setTimeout(() => handleSendMessage(chip.query, chip.intent), 100);
  };

  const handleEndConversation = async () => {
    console.info('🔚 Ending conversation session...');
    localStorage.removeItem('speedbot_session_id');
    setConversation({
      sessionId: '',
      activeIntent: null,
      pendingIntent: null,
      conversationState: null,
      pendingWorkflow: null,
      missingFields: [],
    });
    setMessages([]);
    setActiveSuggestions([]);
    setAttachedFiles([]);
  };

  const handleClearChat = useCallback(() => {
    console.info('🧹 Resetting chatbot state (Clear Chat)...');
    localStorage.removeItem('speedbot_session_id');
    setConversation({
      sessionId: '',
      activeIntent: null,
      pendingIntent: null,
      conversationState: null,
      pendingWorkflow: null,
      missingFields: [],
    });
    setMessages([]);
    setActiveSuggestions([]);
    setAttachedFiles([]);
  }, []);

  const triggerClearChatConfirm = useCallback(() => {
    const confirmMsg: BotMessage = {
      id: generateLocalMessageId(),
      role: 'bot',
      content: 'Do you want to clear the current chat messages?',
      state: 'CONFIRM_CLEAR_CHAT',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, confirmMsg]);
  }, [generateLocalMessageId]);

  const triggerEndConversationConfirm = useCallback(() => {
    const confirmMsg: BotMessage = {
      id: generateLocalMessageId(),
      role: 'bot',
      content: 'Are you sure you want to end this conversation?',
      state: 'CONFIRM_END_CONVERSATION',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, confirmMsg]);
  }, [generateLocalMessageId]);

  const dismissConfirmation = useCallback((messageId: string) => {
    setMessages(prev => prev.filter(m => m.id !== messageId));
  }, []);

  const handleMissingFieldClick = useCallback((field: string) => {
    setInputValue(prev => {
      const nextVal = appendFieldToDraft(prev, field);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const length = nextVal.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 50);
      return nextVal;
    });
  }, []);

  const handleIntentConfirmation = useCallback(
    async (confirm: boolean) => {
      const intentToSend = confirm ? conversation.pendingIntent : conversation.activeIntent;
      console.info(`🤖 CONFIRM INTENT SWITCH: confirm=${confirm}, intent=${intentToSend}`);
      await handleSendMessage('', intentToSend, confirm);
    },
    [conversation.pendingIntent, conversation.activeIntent, handleSendMessage],
  );

  const getUserInitials = () => {
    if (userName && userName !== 'User') {
      return userName.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return {
    isOpen,
    setIsOpen,
    messages,
    setMessages,
    inputValue,
    setInputValue,
    isLoading,
    setIsLoading,
    attachedFiles,
    setAttachedFiles,
    showSidebar,
    setShowSidebar,
    apiError,
    setApiError,
    uploadProgress,
    setUploadProgress,
    showFileDialog,
    setShowFileDialog,
    chatbotExtractionLoading,
    isActivating,
    setIsActivating,
    sessionId,
    currentBotState,
    conversationHistory,
    messagesEndRef,
    fileInputRef,
    chatWindowRef,
    inputRef,
    handleFileSelect,
    removeFile,
    handleQuickAction,
    handleSendMessage,
    handleBotStateAction,
    handleRelatedChipClick,
    getUserInitials,
    activeSuggestions,
    handleEndConversation,
    handleClearChat,
    triggerClearChatConfirm,
    triggerEndConversationConfirm,
    dismissConfirmation,
    handleMissingFieldClick,
    missingFields,
    activeIntent,
    pendingIntent,
    handleIntentConfirmation,
    activeConfirmation,
    confirmationLoading,
    handleConfirmationAction,
  };
};
